<script setup lang="ts">
  import { reactive, computed, watch, onMounted } from "vue";
  import { Text } from "@ui-kit";
  import { NumberService } from "@src/services";
  import { useShopZoneListStore } from "@src/store";
  import { utils, type MoneymapItemWithTitle, type MoneymapSortMode } from "./utils";
  import { default as MoneymapItemChart } from "./MoneymapItemChart.vue";
  import { StatRange } from "@src/api";

  const {
    zone_id,
    shop_id,
    title,
    amount,
    quantity,
    is_zone,
    is_link,
    selected_range_id,
    selected_sort_id,
    selected_display_graph_id,
  } = defineProps<{
    zone_id: string;
    shop_id: string;
    title: string;
    amount: string;
    quantity: number;
    is_zone: boolean;
    is_link: boolean;
    selected_range_id: string;
    selected_sort_id: string;
    selected_display_graph_id: string;
  }>();
  const emit = defineEmits<{ (e: "click"): void }>();

  const state = reactive<{ data: MoneymapItemWithTitle[] }>({ data: [] });

  const shop_zone_list_store = useShopZoneListStore();

  const moneymapLoad = async () => {
    if (!is_zone) {
      state.data = [
        {
          zone_id,
          amount_total: amount,
          quantity_total: quantity,
          records_total: 0,
          is_link,
          is_zone,
        },
      ];

      return;
    }

    const [range, delta] = selected_range_id.split("_");
    const data = await utils.fetchMoneymapWithZones(
      {
        shop_id,
        range: range as StatRange,
        zone_id,
        delta: delta ? Number.parseInt(delta) : undefined,
      },
      shop_zone_list_store,
    );

    if (data === null) return;

    const zone = shop_zone_list_store.map[zone_id];
    if (!zone) {
      state.data = [];
      return;
    }

    const merged = utils.mergeZoneMoneymapData(zone, data, shop_zone_list_store.map);
    state.data = utils.sortMoneymap(merged, selected_sort_id as MoneymapSortMode);
  };

  const has_nested_sales = computed(() => state.data.some((item) => item.is_zone));
  const max = computed(() => {
    let amount_max = 0;
    let quantity_max = 0;

    state.data.forEach((item) => {
      const _amount = Number.parseInt(item.amount_total);
      const _quantity = item.quantity_total;

      if (amount_max < _amount) amount_max = _amount;
      if (quantity_max < _quantity) quantity_max = _quantity;
    });

    return { amount: amount_max, quantity: quantity_max };
  });

  const handleClickMoneymapZone = () => {
    if (!has_nested_sales.value) return;

    emit("click");
  };

  watch(() => selected_range_id, moneymapLoad);

  watch(
    () => selected_sort_id,
    (sort_id) => {
      if (state.data.length > 0) {
        state.data = utils.sortMoneymap(state.data, sort_id as MoneymapSortMode);
      }
    },
  );

  onMounted(moneymapLoad);
</script>

<template>
  <div class="moneymap__item" :data-zone-id="zone_id" data-testid="moneymap-item" @click="handleClickMoneymapZone">
    <div class="moneymap__item-header">
      <div class="moneymap__item-name">
        <Text font="body-medium-16">{{ is_zone ? title : $t("Вне отдела") }}</Text>
      </div>
      <div class="moneymap__item-details">
        <Text :wrap="false">{{ NumberService.toLocaleCurrency(amount) }}</Text>
        <Text>{{ `${quantity} ${$t("шт")}.` }}</Text>
      </div>
    </div>
    <hr class="moneymap__item__divider" />
    <div v-if="state.data.length > 0" class="moneymap__item-chart-list">
      <MoneymapItemChart
        v-for="item in state.data"
        :key="item.zone_id"
        :title="item.title"
        :amount="item.amount_total"
        :quantity="item.quantity_total"
        :max_amount="max.amount"
        :max_quantity="max.quantity"
        :is_zone="item.is_zone"
        :is_link="item.is_link"
        :selected_display_graph_id="selected_display_graph_id"
      />
    </div>
    <div v-else class="moneymap__item-no-data" data-testid="moneymap-item-no-data">
      <Text color="secondary">{{ `${$t("Нет данных о продажах за")}\n${$t("выбранный период")}` }}</Text>
    </div>
  </div>
</template>

<style scoped>
  .moneymap__item {
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-white);
    border-radius: 16px;
    box-shadow: var(--sf-shadow-bot-s);
  }

  .moneymap__item-header {
    display: flex;
    gap: 4px;
    padding: 16px 16px 12px 16px;
  }

  .moneymap__item-name {
    display: flex;
    height: fit-content;
    padding: 12px 16px;
    border-radius: 12px;
    background-color: var(--sf-base-background-secondary);
  }

  .moneymap__item-details {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: flex-end;
    min-width: 0;

    & :deep(.text) {
      &:first-of-type {
        font-weight: 600;
        font-style: SemiBold;
        font-size: 28px;
        line-height: 36px;
        letter-spacing: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }
  }

  .moneymap__item__divider {
    height: 1px;
    width: 100%;
    background-color: var(--sf-base-border-secondary);
  }

  .moneymap__item-chart-list {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 12px;
  }

  .moneymap__item-no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    background-color: var(--sf-base-background-primary);
    border-radius: 20px;
    margin: 16px;

    & :deep(.text) {
      white-space: pre;
      text-align: center;
    }
  }
</style>
